import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Info, Search as SearchIcon, Sparkles, Mail, Check } from "lucide-react";
import { motion } from "framer-motion";
import { Navbar } from "../Navbar";
import { Footer } from "../Footer";
import { typeLabel, categoryBySlug } from "../../data/hub";
import { track } from "../../lib/analytics";

// Lightweight per-page SEO — sets document title + meta description (no plugin).
export const Seo = ({ title, description, jsonLd }) => {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute("name", "description");
        document.head.appendChild(m);
      }
      m.setAttribute("content", description);
    }
    let script = document.getElementById("seo-jsonld");
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.id = "seo-jsonld";
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }
    return () => {
      const s = document.getElementById("seo-jsonld");
      if (s) s.remove();
    };
  }, [title, description, jsonLd]);
  return null;
};

// Page wrapper for all hub pages: fixed nav clearance + footer + scroll reset.
export const HubPage = ({ children, testid, topClass = "pt-28 sm:pt-32" }) => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="bg-cream min-h-screen" data-testid={testid}>
      <Navbar />
      <main className={topClass}>{children}</main>
      <Footer />
    </div>
  );
};

export const Container = ({ className = "", children }) => (
  <div className={`mx-auto max-w-[1200px] px-6 sm:px-8 lg:px-12 ${className}`}>{children}</div>
);

export const SectionHead = ({ overline, title, sub, className = "" }) => (
  <div className={`max-w-2xl ${className}`}>
    {overline && (
      <span className="text-[0.7rem] font-medium uppercase tracking-[0.28em] text-sage">{overline}</span>
    )}
    <h2 className="serif-display mt-4 text-[1.9rem] sm:text-[2.5rem] font-normal leading-[1.12] text-charcoal">{title}</h2>
    {sub && <p className="mt-4 text-base sm:text-lg leading-relaxed text-ink/70">{sub}</p>}
  </div>
);

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, ease: "easeOut", delay }}
  >
    {children}
  </motion.div>
);

// Full-width tinted band for section background variation.
export const Band = ({ children, tone = "cream", className = "", id }) => {
  const bg =
    tone === "white" ? "bg-white/55"
    : tone === "deep" ? "bg-cream-deep/60"
    : tone === "sage" ? "bg-sage-deep/[0.045]"
    : "bg-transparent";
  return <section id={id} className={`${bg} ${className}`}>{children}</section>;
};

// Editorial photograph in a premium rounded frame (soft ring + warm overlay).
export const HeroMedia = ({ src, alt, className = "", eager = false }) => (
  <div className={`relative overflow-hidden rounded-[1.7rem] border border-line/70 shadow-[0_28px_64px_rgba(47,75,57,0.16)] ${className}`}>
    <img
      src={src}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      className="h-full w-full object-cover"
      data-testid="hero-media-image"
    />
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-cream/25 via-transparent to-transparent" />
  </div>
);

// Category tile — image-led editorial card with per-category accent identity.
export const CategoryCard = ({ cat }) => {
  const a = cat.accent || { tint: "rgba(126,154,107,0.12)", deep: "#3E5A44" };
  return (
    <Link
      to={`/resources/${cat.slug}`}
      onClick={() => track("category_selected", cat.slug)}
      data-testid={`category-card-${cat.slug}`}
      className="group relative flex h-full flex-col overflow-hidden rounded-[1.5rem] border border-line bg-white/80 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_26px_60px_rgba(47,75,57,0.18)]"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        {cat.image ? (
          <img
            src={cat.image}
            alt={`${cat.title} — a calm HEARD editorial scene`}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
          />
        ) : (
          <div className="h-full w-full" style={{ backgroundColor: a.tint }} />
        )}
        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${a.deep}66 0%, transparent 55%)` }} />
        <span className="absolute left-4 top-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-cream/90 shadow-sm backdrop-blur-sm" style={{ color: a.deep }}>
          <cat.icon size={21} strokeWidth={1.7} />
        </span>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <span className="h-0.5 w-8 rounded-full transition-all duration-500 group-hover:w-14" style={{ backgroundColor: a.deep }} />
        <h3 className="mt-3 font-serif text-xl font-semibold leading-snug text-heading">{cat.title}</h3>
        <p className="mt-1.5 flex-1 text-sm leading-relaxed text-ink/65">{cat.blurb}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: a.deep }}>
          Explore <ArrowRight size={13} className="transition-transform duration-500 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
};

// Large editorial featured card — a warm, people-free accent panel drawn from
// the resource's category colour + icon (no stock photos needed).
export const FeaturedCard = ({ resource, eyebrow = "Featured resource", testid }) => {
  const cat = categoryBySlug(resource.category);
  const a = cat?.accent || { tint: "#e9f0e6", deep: "#3E5A44" };
  const Icon = cat?.icon;
  return (
    <Link
      to={`/read/${resource.slug}`}
      onClick={() => track("resource_opened", resource.slug)}
      data-testid={testid || `featured-card-${resource.slug}`}
      className="group grid overflow-hidden rounded-[1.8rem] border border-line bg-white/80 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_28px_64px_rgba(47,75,57,0.16)] lg:grid-cols-[1.15fr_1fr]"
    >
      <div className="order-2 flex flex-col p-8 sm:p-10 lg:order-1">
        <span className="text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-sage">{eyebrow}</span>
        <div className="mt-3"><TypeChip typeKey={resource.type} /></div>
        <h3 className="serif-display mt-4 text-[1.9rem] sm:text-[2.3rem] font-normal leading-[1.08] text-charcoal">{resource.title}</h3>
        <p className="mt-3 max-w-xl text-base leading-relaxed text-ink/75">{resource.dek}</p>
        <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium" style={{ color: a.deep }}>
          Read this guide <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1" />
        </span>
      </div>
      <div
        className="relative order-1 hidden min-h-[220px] overflow-hidden lg:order-2 lg:block"
        style={{ background: `linear-gradient(135deg, ${a.tint} 0%, rgba(255,255,255,0.6) 100%)` }}
      >
        <span className="absolute -right-6 -top-6 h-32 w-32 rounded-full opacity-30" style={{ backgroundColor: a.deep }} />
        <span className="absolute bottom-6 left-8 text-[0.62rem] font-semibold uppercase tracking-[0.2em]" style={{ color: a.deep }}>
          {cat?.title}
        </span>
        {Icon && (
          <Icon
            className="absolute right-8 top-1/2 -translate-y-1/2 transition-transform duration-700 group-hover:scale-110"
            size={130}
            strokeWidth={1}
            style={{ color: a.deep, opacity: 0.85 }}
          />
        )}
      </div>
    </Link>
  );
};

// Life-moment pill card
export const MomentCard = ({ moment }) => (
  <Link
    to={moment.to}
    onClick={() => track("life_moment_selected", moment.label)}
    data-testid={`moment-card-${moment.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
    className="group flex items-center gap-3 rounded-full border border-line bg-white/70 px-5 py-4 text-left transition-all duration-400 hover:border-sage hover:bg-white hover:shadow-[0_10px_26px_rgba(47,75,57,0.08)]"
  >
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sage/12 text-sage-deep">
      <moment.icon size={17} strokeWidth={1.8} />
    </span>
    <span className="text-sm font-medium text-charcoal">{moment.label}</span>
    <ArrowRight size={14} className="ml-auto text-sage-deep opacity-0 transition-opacity group-hover:opacity-100" />
  </Link>
);

// Resource / article card — subtle category accent on the type chip.
export const ResourceCard = ({ resource }) => {
  const a = categoryBySlug(resource.category)?.accent || { tint: "rgba(126,154,107,0.1)", deep: "#3E5A44" };
  return (
    <Link
      to={`/read/${resource.slug}`}
      onClick={() => track("resource_opened", resource.slug)}
      data-testid={`resource-card-${resource.slug}`}
      className="group flex h-full flex-col rounded-[1.4rem] border border-line bg-white/80 p-6 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_48px_rgba(47,75,57,0.12)]"
    >
      <span
        className="inline-flex w-fit items-center rounded-full px-3 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.16em]"
        style={{ backgroundColor: a.tint, color: a.deep }}
      >
        {typeLabel(resource.type)}
      </span>
      <h3 className="serif-display mt-4 text-[1.5rem] font-normal leading-snug text-charcoal">{resource.title}</h3>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-ink/70">{resource.dek}</p>
      <span className="mt-5 flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-taupe">
        {resource.readTime} read
        <ArrowRight size={13} className="transition-transform duration-500 group-hover:translate-x-1" style={{ color: a.deep }} />
      </span>
    </Link>
  );
};

export const Disclaimer = () => (
  <div
    className="flex items-start gap-3 rounded-2xl border border-line bg-cream-deep/50 px-5 py-4 text-sm leading-relaxed text-ink/70"
    data-testid="edu-disclaimer"
  >
    <Info size={17} className="mt-0.5 shrink-0 text-sage-deep" strokeWidth={1.8} />
    <p>
      This is general educational information — not individualized medical, nutritional, financial, or professional
      advice. For guidance about your specific situation, please consult a qualified professional.
    </p>
  </div>
);

// CTA back to the HEARD app/community
export const CommunityCTA = ({ compact = false }) => (
  <div
    className={`rounded-[1.6rem] border border-line bg-sage-deep/95 text-cream ${compact ? "p-6" : "p-8 sm:p-10"}`}
    data-testid="community-cta"
  >
    <p className="text-[0.7rem] uppercase tracking-[0.24em] text-cream/70">Keep going, together</p>
    <h3 className="serif-display mt-3 text-2xl sm:text-3xl font-normal">Join the conversation in HEARD.</h3>
    <p className="mt-3 max-w-md text-sm leading-relaxed text-cream/80">
      Resources are a start. Real support happens in community — anonymous, kind, and always human.
    </p>
    <Link
      to="/community"
      onClick={() => track("join_community_clicked", "community-cta")}
      data-testid="community-cta-btn"
      className="mt-6 inline-flex items-center gap-2 rounded-full bg-cream px-6 py-3 text-sm font-medium uppercase tracking-[0.14em] text-charcoal transition-colors hover:bg-white"
    >
      Explore the community <ArrowRight size={15} />
    </Link>
  </div>
);

// Gentle newsletter capture — separate from the beta/early-access flow.
export const NewsletterSignup = ({ variant = "card", source = "" }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      await fetch(`${API}/api/newsletter-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), first_name: name.trim(), source }),
      });
      track("newsletter_signup", source);
      setDone(true);
    } catch (err) {
      setDone(true);
    } finally {
      setBusy(false);
    }
  };

  const isFooter = variant === "footer";

  if (done) {
    return (
      <div
        className={`flex items-center gap-3 rounded-2xl px-5 py-4 text-sm ${isFooter ? "bg-white/10 text-cream" : "border border-sage/25 bg-sage/8 text-sage-deep"}`}
        data-testid="newsletter-success"
      >
        <Check size={18} className="shrink-0" />
        <span>Thank you — a little HEARD is on its way to your inbox. 🌿</span>
      </div>
    );
  }

  return (
    <div
      className={
        isFooter
          ? ""
          : "rounded-[1.6rem] border border-sage-deep/20 bg-cream-deep/50 p-7 sm:p-9"
      }
      data-testid="newsletter-signup"
    >
      {!isFooter && (
        <>
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sage/15 text-sage-deep">
            <Mail size={19} strokeWidth={1.8} />
          </span>
          <h3 className="serif-display mt-4 text-2xl text-charcoal">A little HEARD in your inbox.</h3>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-ink/70">
            Thoughtful resources, encouragement, and things worth knowing — delivered gently.
          </p>
        </>
      )}
      {isFooter && (
        <p className="mb-3 text-sm leading-relaxed text-cream/75">
          <span className="font-medium text-cream">A little HEARD in your inbox.</span> Thoughtful resources, delivered gently.
        </p>
      )}
      <form onSubmit={submit} className="mt-4 flex flex-col gap-3 sm:flex-row" data-testid="newsletter-form">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="First name (optional)"
          className={`w-full rounded-full border px-5 py-3 text-sm outline-none sm:w-40 ${isFooter ? "border-cream/20 bg-white/10 text-cream placeholder:text-cream/50" : "border-line bg-white text-ink placeholder:text-ink/40 focus:border-sage"}`}
          data-testid="newsletter-name-input"
        />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          className={`w-full flex-1 rounded-full border px-5 py-3 text-sm outline-none ${isFooter ? "border-cream/20 bg-white/10 text-cream placeholder:text-cream/50" : "border-line bg-white text-ink placeholder:text-ink/40 focus:border-sage"}`}
          data-testid="newsletter-email-input"
        />
        <button
          type="submit"
          disabled={busy}
          className="whitespace-nowrap rounded-full bg-sage-deep px-6 py-3 text-xs font-medium uppercase tracking-[0.14em] text-cream transition-colors hover:bg-sage disabled:opacity-60"
          data-testid="newsletter-submit"
        >
          {busy ? "Sending…" : "Subscribe"}
        </button>
      </form>
      <p className={`mt-3 text-[0.7rem] ${isFooter ? "text-cream/50" : "text-ink/50"}`}>No spam, ever. Unsubscribe anytime.</p>
    </div>
  );
};

// Simple search box — routes to /explore?q=
export const SearchBox = ({ defaultValue = "", onSubmit, autoFocus = false }) => (
  <form
    onSubmit={onSubmit}
    className="flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2.5 shadow-sm focus-within:border-sage"
    data-testid="hub-search-form"
  >
    <SearchIcon size={18} className="text-sage-deep" />
    <input
      name="q"
      defaultValue={defaultValue}
      autoFocus={autoFocus}
      placeholder="Search resources & topics…"
      className="w-full bg-transparent text-sm text-ink outline-none placeholder:text-ink/40"
      data-testid="hub-search-input"
    />
    <button type="submit" className="rounded-full bg-sage-deep px-4 py-1.5 text-xs uppercase tracking-[0.12em] text-cream" data-testid="hub-search-submit">
      Search
    </button>
  </form>
);

// Small content-type label chip (reused on cards & filters)
export const TypeChip = ({ typeKey, active = false, className = "" }) => (
  <span
    className={`inline-flex w-fit items-center rounded-full border px-3 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.16em] transition-colors ${
      active ? "border-sage-deep bg-sage-deep text-cream" : "border-sage/25 bg-sage/10 text-sage-deep"
    } ${className}`}
    data-testid={`type-chip-${typeKey}`}
  >
    {typeLabel(typeKey)}
  </span>
);

// Subtle early-access / beta CTA strip. HEARD is in beta — this nudges users
// toward the current early-access flow (Home #get-app), never dominating content.
// Structured so it can later be swapped for App Store / Google Play launch CTAs.
export const BetaCTA = ({ className = "" }) => {
  const navigate = useNavigate();
  const join = () => {
    track("get_app_clicked", "beta-cta");
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById("beta")?.scrollIntoView({ behavior: "smooth", block: "start" }), 260);
    } else {
      document.getElementById("beta")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  return (
    <div
      className={`flex flex-col gap-4 rounded-[1.4rem] border border-sage-deep/20 bg-cream-deep/50 p-6 sm:flex-row sm:items-center sm:justify-between ${className}`}
      data-testid="beta-cta"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sage/15 text-sage-deep">
          <Sparkles size={17} strokeWidth={1.8} />
        </span>
        <div>
          <p className="text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-sage-deep">Early Access · Founding Community</p>
          <p className="mt-1 text-sm leading-relaxed text-ink/75">
            The HEARD app is in beta. Join early access and help shape a kinder community.
          </p>
        </div>
      </div>
      <button
        onClick={join}
        data-testid="beta-cta-btn"
        className="inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-full bg-sage-deep px-6 py-3 text-xs font-medium uppercase tracking-[0.16em] text-cream transition-colors hover:bg-sage"
      >
        Join Early Access <ArrowRight size={14} />
      </button>
    </div>
  );
};
